import React, { useEffect, useState } from "react";
import { usersResource } from "../../helpers/configRoutes/backend_helper";
import { Link, useNavigate } from "react-router-dom";
import ConfirmDeleteModal from "../../components/modal/ConfirmDeleteModal";
import { useSelector } from "react-redux";

const Team = () => {
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.Login.user);
  const [modalConfirm, setModalConfirm] = useState({
    show: false,
    title: "Delete user",
    loading: false,
    id: null,
  });
  const [filters] = useState({ perPage: 10, page: 1, internal: true });
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const getUsers = async () => {
      const response = await usersResource("get", { params: filters });
      if (response.status) {
        setUsers(response.users);
      }
    };
    getUsers();
  }, [filters]);

  const deleteUser = async () => {
    setModalConfirm((prev) => ({ ...prev, loading: true }));
    const response = await usersResource("del", {}, modalConfirm.id);
    if (response.status) {
      setUsers((prev) => prev.filter((u) => u.id !== modalConfirm.id));
    }
    setModalConfirm({ show: false, title: "Delete user", loading: false, id: null });
  };

  const deactivateUser = async () => {
    setModalConfirm((prev) => ({ ...prev, loading: true }));

    const targetUser = users.find((item) => item.id === modalConfirm.id);
    if (!targetUser) {
      setModalConfirm({ show: false, title: "Change user status", loading: false, id: null });
      return;
    }

    const response = await usersResource(
      "put",
      {
        ...targetUser,
        active: false,
      },
      modalConfirm.id
    );

    if (response.status) {
      setUsers((prev) =>
        prev.map((item) =>
          item.id === modalConfirm.id
            ? {
                ...item,
                active: false,
              }
            : item
        )
      );
    }

    setModalConfirm({ show: false, title: "Change user status", loading: false, id: null });
  };

  return (
    <>
      <div className="min-h-full bg-slate-50 p-6">
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800">Team Members</h1>
            <p className="mt-1 text-sm text-slate-500">
              Manage the internal users that have access to the admin workspace.
            </p>
          </div>

          <Link
            to="/team-upadd"
            className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
          >
            + Add Member
          </Link>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed text-sm">
              <thead className="border-b border-slate-200 bg-slate-100 text-slate-600">
                <tr>
                  <th className="w-[260px] px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide">User</th>
                  <th className="w-[260px] px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide">Email</th>
                  <th className="w-[140px] px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide">Status</th>
                  <th className="w-[120px] px-5 py-4 text-right text-xs font-semibold uppercase tracking-wide">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200">
                {users.map((u) => (
                  <tr key={u.id} className="transition hover:bg-slate-50">
                    <td className="px-5 py-4 align-middle">
                      <div className="flex min-w-0 items-center gap-3">
                        <img
                          src={u.imageProfile || "/images/usrAnonimo.png"}
                          alt={`${u.first_name || "User"} ${u.last_name || ""}`.trim()}
                          className="h-11 w-11 shrink-0 rounded-full border border-slate-200 object-cover bg-slate-100"
                        />
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-slate-800">
                            {[u.first_name, u.last_name].filter(Boolean).join(" ") || "-"}
                          </p>
                          <p className="truncate text-xs text-slate-500">ID: {u.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 align-middle text-slate-600">
                      <span className="block truncate">{u.email || "-"}</span>
                    </td>

                    <td className="px-5 py-4 align-middle">
                      <button
                        type="button"
                        disabled={!u.active || !u.can?.disable || authUser?.id === u.id}
                        onClick={() =>
                          setModalConfirm({
                            show: true,
                            title: "Change user status",
                            text: `Are you sure you want to deactivate ${
                              [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || "this user"
                            }?`,
                            textButton: "Deactivate",
                            textLoad: "Deactivating",
                            loading: false,
                            id: u.id,
                            actionType: "deactivate",
                          })
                        }
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-medium transition ${
                          u.active
                            ? !u.can?.disable || authUser?.id === u.id
                              ? "cursor-not-allowed bg-emerald-100 text-emerald-700 opacity-70"
                              : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                            : "cursor-not-allowed bg-rose-100 text-rose-700"
                        }`}
                        title={
                          authUser?.id === u.id
                            ? "You cannot deactivate your own user"
                            : u.active && u.can?.disable
                              ? "Click to deactivate"
                              : "Inactive"
                        }
                      >
                        {u.active ? "Active" : "Inactive"}
                      </button>
                    </td>

                    <td className="px-5 py-4 align-middle">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => navigate(`/team-upadd/${u.id}`)}
                          className="rounded-lg p-2 text-slate-500 transition hover:bg-blue-50 hover:text-blue-600"
                          title="Edit"
                        >
                          <i className="fas fa-pen"></i>
                        </button>

                        <button
                          disabled={!u.can?.delete}
                          onClick={() =>
                            setModalConfirm({
                              show: true,
                              title: "Delete user",
                              loading: false,
                              id: u.id,
                            })
                          }
                          className={`rounded-lg p-2 transition ${
                            u.can?.delete
                              ? "text-slate-500 hover:bg-rose-50 hover:text-rose-600"
                              : "cursor-not-allowed text-slate-300"
                          }`}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {users.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500">No users found</div>
          )}
        </div>
      </div>

      <ConfirmDeleteModal
        modal={modalConfirm}
        setModal={setModalConfirm}
        action={modalConfirm.actionType === "deactivate" ? deactivateUser : deleteUser}
      />
    </>
  );
};

export default Team;
